import React from 'react';
import { Card, Row, Col, Statistic, Typography, Space, Button } from 'antd';
import {
  TeamOutlined,
  ShoppingCartOutlined,
  InboxOutlined,
  ToolOutlined,
  RightOutlined,
  UserAddOutlined,
  ShoppingOutlined,
  DollarOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';

const { Title, Text, Paragraph } = Typography;

export const CoreOperationsDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = React.useState(window.innerWidth < 768);

  React.useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const modules = [
    {
      title: 'CRM & Growth',
      icon: <TeamOutlined style={{ fontSize: '32px', color: '#145A54' }} />,
      description: 'Manage leads, opportunities, customers and customer orders',
      stats: { label: 'Active Leads', value: 45 },
      quickLinks: [
        { label: 'Leads', path: '/growth/leads' },
        { label: 'Opportunities', path: '/crm/opportunities' },
        { label: 'Customers', path: '/growth/customers' },
      ],
      mainPath: '/sales/leads',
    },
    {
      title: 'Cost Estimations',
      icon: <DollarOutlined style={{ fontSize: '32px', color: '#52c41a' }} />,
      description: 'Create BOQs, estimates, and manage pricing with AI predictions',
      stats: { label: 'Active Estimations', value: 12 },
      quickLinks: [
        { label: 'Dashboard', path: '/crm/estimations' },
        { label: 'BOQ Estimations', path: '/estimation/boq' },
        { label: 'AI Predictor', path: '/estimation/ai-predictor' },
      ],
      mainPath: '/crm/estimations',
    },
    {
      title: 'Procurement',
      icon: <ShoppingCartOutlined style={{ fontSize: '32px', color: '#fa8c16' }} />,
      description: 'Manage suppliers, purchase orders, RFQs and goods receipt',
      stats: { label: 'Open POs', value: 23 },
      quickLinks: [
        { label: 'Suppliers', path: '/procurement/suppliers' },
        { label: 'Purchase Orders', path: '/procurement/orders' },
        { label: 'RFQs', path: '/procurement/rfqs' },
      ],
      mainPath: '/procurement/suppliers',
    },
    {
      title: 'Inventory',
      icon: <InboxOutlined style={{ fontSize: '32px', color: '#722ed1' }} />,
      description: 'Track items, warehouses, stock levels and movements',
      stats: { label: 'Items in Stock', value: 856 },
      quickLinks: [
        { label: 'Items', path: '/inventory/items' },
        { label: 'Warehouses', path: '/inventory/warehouses' },
        { label: 'Stock Summary', path: '/inventory/stock-summary' },
      ],
      mainPath: '/inventory/items',
    },
    {
      title: 'Manufacturing',
      icon: <ToolOutlined style={{ fontSize: '32px', color: '#eb2f96' }} />,
      description: 'Manage BOMs, work orders, production scheduling and capacity',
      stats: { label: 'Active Work Orders', value: 18 },
      quickLinks: [
        { label: 'Work Orders', path: '/manufacturing/work-orders' },
        { label: 'BOMs', path: '/manufacturing/boms' },
        { label: 'Scheduling', path: '/manufacturing/job-scheduling' },
      ],
      mainPath: '/manufacturing/work-orders',
    },
  ];

  return (
    <div>
      <Space direction="vertical" size={isMobile ? 'middle' : 'large'} style={{ width: '100%' }}>
        <div>
          <Title level={isMobile ? 3 : 2}>Core Operations</Title>
          <Paragraph type="secondary" style={{ fontSize: isMobile ? '13px' : '14px' }}>
            Quick access to sales, procurement, inventory and manufacturing modules
          </Paragraph>
        </div>

        <Row gutter={isMobile ? [16, 16] : [24, 24]}>
          {modules.map((module, index) => (
            <Col xs={24} md={12} lg={8} key={index}>
              <Card
                hoverable
                onClick={() => navigate(module.mainPath)}
                style={{ height: '100%' }}
                actions={module.quickLinks.map(link => (
                  <Button
                    type="text"
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(link.path);
                    }}
                    key={link.path}
                  >
                    {link.label}
                  </Button>
                ))}
              >
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    {module.icon}
                    <RightOutlined style={{ color: '#8c8c8c' }} />
                  </div>
                  <div>
                    <Title level={4} style={{ marginBottom: 8 }}>{module.title}</Title>
                    <Text type="secondary">{module.description}</Text>
                  </div>
                  <Statistic 
                    title={module.stats.label} 
                    value={module.stats.value}
                    valueStyle={{ fontSize: '24px', fontWeight: 'bold' }}
                  />
                </Space>
              </Card>
            </Col>
          ))}
        </Row>
      </Space>
    </div>
  );
};

